/*
<SelectInput /> Provides a style-able <select> replacement, optionally editable as an <input> as well.

  Props:
      options    list of strings or objects to present as options
                 If using objects, you will need to provide the *Renderer and *Extractor props as well

      value      initial value/selected option

      onChange   handler that is called when a new option is selected, or text is entered.
                 beware you may recieve an option you originally passed in, or a string
                  - if an option is selected, then the original option object is provided
                  - if other text is entered, the entered text is provided

      optionRenderer
                 (option)=> ...jsx...
                 custom render method for rending options in the select options list
                 *defaults to optionTextExtractor if one is provided*

      selectedOptionRenderer
                 (input, selectedOption)=> ...jsx...
                 custom render method for rending the currently selected option (or custom user-provided value)
                 *input* is a React component that provides the user input control for typing a value,
                 you can nest this in your custom view to maintain editable functionality.

      optionTextExtractor
                 (option)=> string
                 When providing objects for options, this method will be used to write the text for
                 selected options to the selected option text box, as well as used for extracting the
                 text to search against while the user types.

      placeholder
      id         passed through to the <input /> element representing the current value.
                 note that <Field /> has a special-case handling for <SelectInput /> and will already
                 wire up the <label /> appropriately using this `id` property

      editable   true by default. set to false to make the current value input readonly.

  Examples:
      <SelectInput options={['one', 'two', 'three']}
                   onChange={this.onChange} />

      <SelectInput options={[ {name: 'Joe', age: 19}, {name: 'Sue', age: 20} ]}
                   optionTextExtractor={ (option)=> option.name }
                   optionRenderer={ (option)=> <div>{option.name} <i>{opton.age}</i></div> }
                   selectedOptionRenderer={ (input, option)=> <div>{input} <i>{option.age}</i></div> }
*/
import { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';

const key= {
  down: 40,
  end: 35,
  enter: 13,
  esc: 27,
  home: 36,
  space: 32,
  tab: 9,
  up: 38
};
const nonAlphaNum = /[^a-z0-9]/g;

const defaultOptionRenderer = (o)=> o,
      defaultSelectedOptionRenderer = (i)=> i,
      defaultOptionTextExtractor = (o)=> o;

// TODO: needs a clear button to remove value. especially when editable==false

const SelectInput = (props) => {

  const {options, optionsByText} = prepOptions(props.options, props.optionTextExtractor);
  const [state, setState] = useState({
    value: props.value || '',
    picklist_visible: false,
    selected_option: null,
    selected_option_index: -1,
    options, optionsByText,
    filtered_in: options || [],
    filtered_out: [],
  });
  const optionsEl = useRef(null);
  const inputEl = useRef(null);
  const blur_timeout = useRef(null);

  useEffect(() => {
    if(optionsEl.current){
      const selectedEl = optionsEl.current.querySelector('.selected');
      selectedEl ? selectedEl.scrollIntoView() : optionsEl.current.scrollTop = 0;
    }
  },[]);

  function prepOptions(in_options, extractor){
    extractor = extractor || defaultOptionTextExtractor;
    const optionsByText = {},
          options = (in_options || []).map( (option)=> {
            const text = extractor(option),
                  filterKey = text.toLowerCase().replace(nonAlphaNum, '');
            optionsByText[text] = option;
            return { _orig:option, text, filterKey }
          });
    return { options, optionsByText };
  }

  function onFocus(ev){
    if(blur_timeout.current) clearTimeout(blur_timeout.current);
    setState(prev => ({...prev, picklist_visible: true}));
  }

  function onBlur(ev){
    blur_timeout.current = setTimeout(() => setState(prev => ({...prev, picklist_visible: false})), 100);
  }

  function onClick(ev){
    setState(prev => ({...prev, picklist_visible: true}));
  }

  function onHandleClick(ev){
    inputEl.current?.focus();
    setState(prev => ({...prev, picklist_visible: !prev.picklist_visible}));
  }

  function selectOption(option, index, commit=false){
    if(commit) {
      filterValues(option.text);
      setState(prev => ({...prev, value: option._orig, picklist_visible: false, selected_option: null, selected_option_index: -1}));
      props.onChange && props.onChange(option._orig);
    } else {
      setState(prev => ({...prev, selected_option: option, selected_option_index: index}));
    }
    inputEl.current?.focus();
  }

  function onChange(ev){
    const textValue = ev.target.value,
          matchingOption = state.optionsByText[textValue],
          value = matchingOption || textValue;
    setState(prev => ({...prev, value: value}));
    props.onChange && props.onChange(value);
  }

  function onKeyDown(ev){
    switch(ev.which){
      case key.down:
        ev.preventDefault();
        if(state.options){
          setState(prev => ({...prev, picklist_visible: true}));
          if(state.picklist_visible) {
            const next_index = (state.selected_option_index + 1 ) % state.options.length,
                  next_option = next_index > state.filtered_in.length - 1
                      ? state.filtered_out[next_index-state.filtered_in.length]
                      : state.filtered_in[next_index];
            selectOption(next_option, next_index);
          }
        }
        break;
      case key.up:
        ev.preventDefault();
        if(props.options){
          setState(prev => ({...prev, picklist_visible: true}));
          if(state.picklist_visible) {
            const prev_index = state.selected_option_index > 0
                    ? state.selected_option_index - 1
                    : state.options.length - 1,
                  prev_option = prev_index > state.filtered_in.length - 1
                    ? state.filtered_out[prev_index-state.filtered_in.length]
                    : state.filtered_in[prev_index];
            selectOption(prev_option, prev_index);
          }
        }
        break;
      case key.enter:
      case key.space:
        if(state.picklist_visible && state.selected_option) {
          ev.preventDefault();
          selectOption(state.selected_option, state.selected_option_index, true);
        }
        if(state.picklist_visible && !state.selected_option && ev.which === key.enter ) {
          setState(prev => ({...prev, picklist_visible: false}));
        }
        break;
      case key.esc:
        if(state.picklist_visible){
          ev.preventDefault();
          setState(prev => ({...prev, picklist_visible: false}));
        }
        break;
      default:
        setState(prev => ({...prev, picklist_visible: true}));
    }
  }

  function onKeyUp(ev){
    filterValues(ev.target.value);
  }

  function filterValues(filter_value){
    filter_value = (filter_value || '').toLowerCase().replace(nonAlphaNum, '');
    const all_options = state.options || [];
    let filtered_in = all_options;
    let filtered_out = [];
    if(filter_value){
      filtered_in = [];
      for(let option of all_options){
        if(option.filterKey.indexOf(filter_value) > -1 ){
          filtered_in.push(option);
        } else {
          filtered_out.push(option);
        }
      }
    }
    setState(prev => ({...prev, filtered_in, filtered_out}));
  }

  const optionRenderer = props.optionRenderer || props.optionTextExtractor || defaultOptionRenderer;
  const selectedOptionRenderer = props.selectedOptionRenderer || defaultSelectedOptionRenderer;
  const optionTextExtractor = (o)=> typeof o === 'string' ? o : typeof o._orig=== 'string' ? o._orig: (props.optionTextExtractor || defaultOptionTextExtractor)(o);

  const input = <input placeholder={props.placeholder} id={props.id}
                        onFocus={(ev) => onFocus(ev)} 
                        onBlur={(ev) => onBlur(ev)}
                        onClick={(ev) => onClick(ev)} 
                        className='select-input-input'
                        value={optionTextExtractor(state.value)} 
                        onChange={(ev) => onChange(ev)}
                        title={optionTextExtractor(state.value)} 
                        readOnly={props.editable === false}
                        onKeyDown={(ev) => onKeyDown(ev)} 
                        onKeyUp={(ev) => onKeyUp(ev)}
                        ref={(el)=> inputEl.current = el} />;

  return (
      <>
        { selectedOptionRenderer(input, state.value) }
        { props.options && props.options.length > 0 &&
          <>
            <div className={classNames('select-input-options', {'is-open': state.picklist_visible, 'is-filtered': state.filtered_out.length})}
                  ref={(el)=> optionsEl.current = el}>
              <div className='filtered-in'>
                {state.filtered_in.map((option, i)=>
                  <div key={i} className={classNames('select-input-option', {'selected': state.selected_option===option})}
                        onMouseDown={(ev)=> {ev.preventDefault(); selectOption(option, i, true)}}>
                    {optionRenderer(option._orig, state.filtered_out.length > 0)}
                  </div>)}
              </div>
              <div className='filtered-out'>
                {state.filtered_out.map((option, i) =>
                  <div className={classNames('select-input-option', {'selected': state.selected_option===option})}
                        key={i} onMouseDown={(ev)=> {ev.preventDefault(); selectOption(option, state.filtered_in.length + i, true)}}>
                    {optionRenderer(option._orig, false)}
                  </div>)}
              </div>
            </div>
            <span className='drop-handle' onClick={(ev) => onHandleClick(ev)} />
          </>
        }
      </>
  );

}
export default SelectInput;
